import { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Wrench, CheckCircle2, AlertCircle, Car } from "lucide-react";
import { toast } from "sonner";
import { DatePicker } from "@/components/DatePicker";

export default function RevisioneUpdate() {
  const nav = useNavigate();
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newDate, setNewDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
    axios.get(`${apiBase}/public/revisione/${token}`)
      .then((res) => setData(res.data))
      .catch((e) => setError(e?.response?.data?.detail || "Link non valido"))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async () => {
    if (!newDate) { toast.error("Seleziona la nuova data di revisione"); return; }
    setBusy(true);
    try {
      const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
      await axios.post(`${apiBase}/public/revisione/${token}`, { next_review_date: newDate });
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  const minDateISO = (() => {
    const t = new Date(); t.setDate(t.getDate() + 1);
    return t.toISOString().slice(0, 10);
  })();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-400" data-testid="revisione-loading">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Caricamento…
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
        <Card className="max-w-md w-full" data-testid="revisione-error">
          <CardContent className="pt-8 pb-6 text-center">
            <AlertCircle className="h-12 w-12 text-rose-500 mx-auto mb-3" />
            <h2 className="font-display text-xl font-bold mb-2">Link non valido</h2>
            <p className="text-sm text-slate-600">{error}</p>
            <p className="text-xs text-slate-400 mt-4">Se il link è scaduto, contatta la tua agenzia.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-emerald-50 via-white to-blue-50">
        <Card className="max-w-md w-full" data-testid="revisione-success">
          <CardContent className="pt-8 pb-6 text-center">
            <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto mb-3" />
            <h2 className="font-display text-xl font-bold mb-2">Data aggiornata!</h2>
            <p className="text-sm text-slate-600 mb-2">
              Grazie {data?.client_name?.trim() || ""}. La nuova data di revisione è stata salvata.
              Ti invieremo un nuovo promemoria 30 e 7 giorni prima della prossima scadenza.
            </p>
            <Button onClick={() => nav("/")} className="mt-4 bg-emerald-600 hover:bg-emerald-700">
              Chiudi
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full" data-testid="revisione-page">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-display text-xl">
            <Wrench className="h-6 w-6 text-blue-600" /> Aggiorna data revisione
          </CardTitle>
          <p className="text-xs text-slate-500">
            Ciao <strong>{data?.client_name || ""}</strong>, inserisci la nuova data di scadenza
            della revisione del tuo veicolo per continuare a ricevere i promemoria.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg border border-blue-200 bg-blue-50/60 p-3 text-sm">
            <div className="flex items-center gap-2 font-semibold text-blue-900 mb-1">
              <Car className="h-4 w-4" /> Il tuo veicolo
            </div>
            <div className="text-xs text-blue-800 space-y-0.5">
              <div>Targa: <strong className="font-mono">{data?.vehicle_plate || "—"}</strong></div>
              <div>Modello: <strong>{[data?.vehicle_brand, data?.vehicle_model].filter(Boolean).join(" ") || "—"}</strong></div>
              {data?.policy_number && <div>Polizza: <strong>{data.policy_number}</strong></div>}
            </div>
          </div>
          {data?.current_review_date && (
            <p className="text-xs text-slate-500">
              Data revisione precedente: <strong>{new Date(data.current_review_date).toLocaleDateString("it-IT")}</strong>
            </p>
          )}
          <div>
            <Label>Nuova data di scadenza revisione <span className="text-red-500">*</span></Label>
            <DatePicker data-testid="revisione-new-date" min={minDateISO}
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              className="font-mono" />
            <p className="text-[11px] text-slate-400 mt-1">
              💡 In Italia la revisione vale 2 anni per auto già revisionate (4 anni per auto nuove).
              Se non aggiorni la data, il sistema smetterà di inviarti promemoria.
            </p>
          </div>
          <Button
            data-testid="revisione-submit"
            onClick={submit}
            disabled={busy || !newDate}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
            Conferma nuova data
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
